import React, { useEffect, useState } from "react";
import { ProactiveAuditItem } from "../../models/weeklyTimeline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faXmark, faPenToSquare, faCalendar, faUser, faBox, faExclamationTriangle, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import { downloadFile, getTimezoneTime } from "./timeline-items/utils";
import { useAppDispatch } from "../../reducers/store";
import { getSupplierDataById, getLocationById } from "../../reducers/supplierSlice";
import { Supplier } from "../../models/supplier";
import { Location as TQSLocation } from "../../models/location";

interface ProactiveAuditDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditItem: ProactiveAuditItem | null;
  timezone: string;
}

const ProactiveAuditDetailsModal: React.FC<ProactiveAuditDetailsModalProps> = ({
  isOpen,
  onClose,
  auditItem,
  timezone,
}) => {
  const dispatch = useAppDispatch();
  const [fullSupplier, setFullSupplier] = useState<Supplier | null>(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [fullLocation, setFullLocation] = useState<TQSLocation | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Fetch full supplier and location data when modal opens
  useEffect(() => {
    if (isOpen && auditItem) {
      const abortController = new AbortController();
      
      // Fetch supplier data
      if (typeof auditItem.audit.supplier === 'number') {
        setSupplierLoading(true);
        
        dispatch(getSupplierDataById({ 
          supplierId: auditItem.audit.supplier, 
          signal: abortController.signal 
        }))
        .unwrap()
        .then((supplierData) => {
          setFullSupplier(supplierData);
          setSupplierLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching supplier data:', error);
          setSupplierLoading(false);
        });
      }

      // Fetch location data
      if (typeof auditItem.audit.dailyChecklist?.location === 'number') {
        setLocationLoading(true);
        
        dispatch(getLocationById(auditItem.audit.dailyChecklist.location))
        .unwrap()
        .then((locationData) => {
          setFullLocation(locationData);
          setLocationLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching location data:', error);
          setLocationLoading(false);
        });
      }

      return () => {
        abortController.abort();
      };
    }
  }, [isOpen, auditItem, dispatch]);

  if (!isOpen || !auditItem) {
    return null;
  }

  const { audit } = auditItem;
  const issueDetails = audit.issueDetails || "No additional details provided";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#af52de]">
              <FontAwesomeIcon className="h-5 w-5 text-white" icon={faPenToSquare} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Proactive Audit Details</h2>
              <p className="text-sm text-gray-500">
                {getTimezoneTime(auditItem.time, timezone)} {timezone}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Status Section */}
            <div className="flex items-center space-x-3 ml-2">
              {audit.issues ? (
                <div className="flex items-center space-x-2 px-3 py-2 rounded-md border border-red-200 bg-red-50">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-800">Issue Found</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 px-3 py-2 rounded-md border border-green-200 bg-green-50">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">No Issue Found</span>
                </div>
              )}
              {fullLocation?.name && (
                <>
                  <span className="text-gray-400">-</span>
                  <span className="text-lg font-medium text-gray-900">{fullLocation.name}</span>
                </>
              )}
              {locationLoading && (
                <>
                  <span className="text-gray-400">-</span>
                  <span className="text-lg font-medium text-gray-500">(Loading location...)</span>
                </>
              )}
            </div>

            {/* Part Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faBox} className="h-5 w-5 mr-2 text-gray-600" />
                Part Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Part Name</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {audit.part.desc || 'No description available'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Part Number</label>
                  <p className="mt-1 text-sm text-gray-900 font-semibold">
                    #{audit.part.number || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Part Description</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {audit.part.desc || 'No description available'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {audit.part.category || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Platform</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {audit.part.platform || 'Not specified'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Production Team</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {audit.part.productionTeam || 'Not assigned'}
                  </p>
                </div>
              </div>
            </div>

            {/* Supplier Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faUser} className="h-5 w-5 mr-2 text-gray-600" />
                Supplier Information
                {supplierLoading && (
                  <span className="ml-2 text-sm text-gray-500">(Loading...)</span>
                )}
              </h3>
              {supplierLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading supplier details...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {fullSupplier?.name || (typeof audit.supplier === 'object' ? audit.supplier.name : 'Loading...')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Number</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {fullSupplier?.accountNumber || 'Not available'}
                    </p>
                  </div>
                </div>
              )}
            </div>


            {/* Issue Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faPenToSquare} className="h-5 w-5 mr-2 text-gray-600" />
                Issue Details
              </h3>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">{issueDetails}</p>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faFile} className="h-5 w-5 mr-2 text-gray-600" />
                Attachments ({audit.attachments?.length || 0})
              </h3>
              {audit.attachments && audit.attachments.length > 0 ? (
                <div className="space-y-2">
                  {audit.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FontAwesomeIcon icon={faFile} className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-900 font-medium">
                          {attachment.attachment.filename}
                        </span>
                      </div>
                      <button
                        onClick={() => downloadFile(
                          attachment.attachment.downloadURL,
                          attachment.attachment.filename
                        )}
                        className="flex items-center space-x-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <FontAwesomeIcon icon={faCloudArrowDown} className="h-4 w-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FontAwesomeIcon icon={faFile} className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No attachments available for this audit</p>
                </div>
              )}
            </div>

            {/* Audit Metadata */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <FontAwesomeIcon icon={faCalendar} className="h-5 w-5 mr-2 text-gray-600" />
                Audit Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block font-medium text-gray-700">Created</label>
                  <p className="mt-1 text-gray-900">
                    {getTimezoneTime(audit.createTime.toString(), timezone)} {timezone}
                  </p>
                </div>
                <div>
                  <label className="block font-medium text-gray-700">Last Modified</label>
                  <p className="mt-1 text-gray-900">
                    {getTimezoneTime(audit.modifyTime.toString(), timezone)} {timezone}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProactiveAuditDetailsModal;
